import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, Filter, Mail, MessageCircle, ArrowRight, Upload, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const STATUS_COLORS = {
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  sending: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  running: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  queued: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  error: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div data-testid={testId} className="stat-card p-5 fade-up">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={16} className={accent} />
      </div>
      <div className="font-heading text-3xl font-bold text-slate-50">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1">Overview</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-customers" icon={Users} label="Customers" value={stats?.customers ?? "—"} accent="text-indigo-400" />
        <StatCard testId="stat-audiences" icon={Filter} label="Audiences" value={stats?.audiences ?? "—"} accent="text-blue-400" />
        <StatCard testId="stat-emails-sent" icon={Mail} label="Emails sent" value={stats?.emails_sent ?? "—"} accent="text-amber-400" />
        <StatCard testId="stat-wa-sent" icon={MessageCircle} label="WhatsApp pushed" value={stats?.wa_sent ?? "—"} accent="text-emerald-400" />
      </div>

      {stats && stats.customers === 0 && (
        <div className="bg-[#111827] border border-indigo-500/30 rounded-xl p-6 fade-up">
          <h2 className="font-heading text-lg font-semibold text-slate-50 mb-4">Get started in 3 steps</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Settings, title: "1. Configure Settings", desc: "Add your xSMTP credentials and Pabbly webhook URL.", to: "/settings", id: "onboard-settings" },
              { icon: Upload, title: "2. Import customers", desc: "Upload a CSV of purchases, or wire the incoming webhook.", to: "/customers", id: "onboard-customers" },
              { icon: Filter, title: "3. Build an audience", desc: "Stack filters, watch the live count, save the segment.", to: "/audiences/new", id: "onboard-audience" },
            ].map(({ icon: Icon, title, desc, to, id }) => (
              <Link key={id} data-testid={id} to={to} className="group p-4 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-indigo-500/50 transition-colors">
                <Icon size={18} className="text-indigo-400 mb-2" />
                <div className="font-medium text-slate-100 text-sm mb-1">{title}</div>
                <div className="text-xs text-slate-400 mb-2">{desc}</div>
                <span className="text-xs text-indigo-400 flex items-center gap-1">Go <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" /></span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#111827] border border-slate-800 rounded-xl p-6">
        <h2 className="font-heading text-lg font-semibold text-slate-50 mb-4">Recent campaigns</h2>
        {!stats?.activity?.length ? (
          <p data-testid="no-activity-message" className="text-sm text-slate-500">No campaigns yet. Create your first one from Email or WhatsApp Campaigns.</p>
        ) : (
          <div className="space-y-2">
            {stats.activity.map((a) => (
              <div key={a.id} data-testid="activity-row" className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-800/70">
                <div className="flex items-center gap-3 min-w-0">
                  {a.channel === "email" ? <Mail size={15} className="text-indigo-400 shrink-0" /> : <MessageCircle size={15} className="text-emerald-400 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.audience_name} · {new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs font-mono text-slate-400">{a.sent}/{a.total}</span>
                  <Badge className={`border ${STATUS_COLORS[a.status] || "bg-slate-700/40 text-slate-300 border-slate-600"}`}>{a.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
